/**
 * The Stage's brand panel — what a Drop Hub shows when nothing is playing.
 *
 * The Drop Hub never had a non-streaming Stage occupant: `resolveStage` was
 * called with only `streaming`, so an idle Stage resolved to nothing and
 * `SurfaceStage` fell back to drawing its own identity props. The class existed
 * and no Hub ever put anything in it.
 *
 * Supplied ONLY when the Drop carries brand content of its own. A Drop with no
 * logo and no synopsis has nothing to say here, and `SurfaceStage`'s identity
 * block is the better answer for it than an emptier panel — which is why this
 * returns `undefined` rather than an empty panel.
 *
 * Split out of `DropHubSurface` for the 400-line ceiling.
 */
import React from "react";
import {StageBrandPanel} from "@/surfaces/spine/stageWidgets";
import {stageProps, type SpineDrop} from "@/surfaces/spine/dropSpineData";
import {povPreset} from "@/surfaces/povPresets";
import {useGetLedgerStandings} from "@/hooks/ledger-boards/useGetLedgerStandings";

export interface HubBrandStage {
    /** `stageProps` for this Drop — the Stage's own base props. */
    base: ReturnType<typeof stageProps>;
    /** The idle occupant, or `undefined` when the Drop has no brand content. */
    idleOccupant: React.ReactNode | undefined;
}

export function useHubBrandStage(drop: SpineDrop, rosterCount: number): HubBrandStage {
    const boardId = drop?.sections?.find(s =>
        s.enabled !== false && s.engagementType === "LEDGER_BOARD" && s.engagementId)?.engagementId;
    const {data: board} = useGetLedgerStandings(boardId, {limit: 1});
    const boardPeriodEndsAt = board?.periodEndsAt ?? undefined;

    /* The countdown ticks once a minute while the page stays open. */
    const [now, setNow] = React.useState(() => Date.now());
    React.useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 60_000);
        return () => clearInterval(id);
    }, []);

    const base = React.useMemo(
        () => stageProps({...drop, rosterCount}, now, boardPeriodEndsAt),
        [drop, rosterCount, now, boardPeriodEndsAt],
    );

    const idleOccupant = React.useMemo(() => {
        // "identity" presets rest on SurfaceStage's full identity block — no panel occupant.
        if (povPreset(drop?.pov).stageIdle === "identity") return undefined;
        if (!drop?.logoImageUrl && !drop?.synopsis && !drop?.tagline) return undefined;
        return (
            <StageBrandPanel
                logoUrl={drop?.logoImageUrl}
                name={drop?.name ?? ""}
                /*
                 * The same phase line the identity block would have shown —
                 * resolved once in `stageProps`, so the occupant and the
                 * fallback can never disagree about whether the campaign is
                 * running.
                 */
                phaseLabel={base.phaseLabel}
                synopsis={drop?.synopsis ?? drop?.tagline}
            />
        );
    }, [drop, base.phaseLabel]);

    return {base, idleOccupant};
}
